import React, { useEffect, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import { useDispatch } from 'react-redux';
import { useForm } from '@dotone/react-ui-core/hooks';
import {
  useTranslate,
  useShallowEqualSelector,
  useLocalizedDataSelector,
  useSearchSelector,
} from '@hooks';
import { createTextCreative, updateTextCreative } from '@actions';
import classnames from 'classnames';
import { Row, Col, Form } from 'reactstrap';
import {
  FormGroup as BsFormGroup,
  Input,
  DateTimeSelect,
  ActivePeriodDateTimePicker,
  ControlGroup,
  ModalWithInitialButton as Modal,
  LocaleCheckInput,
  FileUploader,
  TextCreativePreview,
  CardBasic,
  Label,
  DeepLinkInput,
} from '@dotone/react-ui-core';
import { CategorySelect, CurrencySelect, OfferSearchInput, OfferVariantRadioGroup } from '../forms';

const FormGroup = ({ label, required = false, ...otherProps }) => (
  <BsFormGroup row>
    <Col md={4} className="text-left text-md-right">
      <Label className="text-custom text-nowrap" required={required}>
        {label}
      </Label>
    </Col>
    <Col md={8} {...otherProps} />
  </BsFormGroup>
);

const offerSearchParams = { offerVariants: true, categories: true };

const TextCreativeFormButton = ({
  textCreative: defaultValues = {},
  buttonProps,
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const { t, locale } = useTranslate('textCreative', 'model');
  const [isOpen, setIsOpen] = useState(false);
  const currencies = useLocalizedDataSelector(({ currencyList }) => currencyList);

  const isEdit = !!defaultValues.id;

  const { handleSubmit, connectForm, reset, watch, setValue, setError } = useForm({
    defaultValues,
  });

  const {
    submitting,
    isSubmitSuccess,
    status,
    data = { message: {} },
  } = useShallowEqualSelector(({ textCreativeCreate: create, textCreativeUpdate: update }) => ({
    data: defaultValues.id ? update.data : create.data,
    isSubmitSuccess: isEdit ? update.status === 'success' : create.status === 'success',
    submitting: isEdit ? update.isLoading : create.isLoading,
    status: create.status || update.status,
  }));

  const [
    offerId,
    offerVariantId,
    title,
    content1,
    couponCode,
    originalPrice,
    discountPrice,
    buttonText,
    currencyId,
    imageUrl,
    cdnUrl,
    isInfinityTime,
  ] = watch([
    'offerId',
    'offerVariantId',
    'title',
    'content1',
    'couponCode',
    'originalPrice',
    'discountPrice',
    'buttonText',
    'currencyId',
    'imageUrl',
    'cdnUrl',
    'isInfinityTime',
  ]);

  const { data: offers } = useSearchSelector(({ offerSearch }) => offerSearch, {
    ids: [offerId],
    locale,
    active: !isEdit,
    forTextCreative: true,
    ...offerSearchParams,
  });

  const creative = {
    buttonText,
    content1,
    couponCode,
    discountPrice,
    imageUrl,
    originalPrice,
    title,
    currency: currencies.find((currency) => currency.id.toString() === currencyId),
  };

  // Setup for toggling offer variant selection
  const selectedOffer =
    offerId && offers.find((offer) => offer.id.toString() === offerId.toString());
  const offerVariants = selectedOffer?.offerVariants || [];

  const handleSubmitForm = ({ id, ...values }) => {
    const params = { ...values, cdnUrl: values.imageUrl };

    if (id) {
      dispatch(updateTextCreative(id, params));
    } else {
      dispatch(createTextCreative(params));
    }
  };

  const onImageUpload = (values) => {
    setValue('imageUrl', values.cdnUrl);
  };

  useEffect(() => {
    if (selectedOffer?.destinationUrl) {
      setValue('clientUrl', selectedOffer.destinationUrl);
    }
  }, [setValue, selectedOffer?.destinationUrl]);

  useDeepCompareEffect(() => {
    // If only one offer variant available, auto-select it
    if (selectedOffer?.id) {
      reset({
        ...defaultValues,
        offerVariantId: offerVariants[0]?.id,
        offerId: selectedOffer?.id,
        imageUrl: cdnUrl || selectedOffer.brandImageUrl,
      });
    }
  }, [selectedOffer?.id, defaultValues, reset]);

  useEffect(() => {
    if (status === 'error' && data.message) {
      Object.keys(data.message).forEach((key) =>
        setError(key, { type: 'custom', message: data.message[key] })
      );
    }
  }, [status, data.message, setError]);

  useEffect(() => {
    if (!submitting && isSubmitSuccess) {
      setIsOpen(false);
    }
  }, [submitting, isSubmitSuccess]);

  useEffect(() => {
    const ids = selectedOffer?.categories?.map(({ id }) => id) || [];
    if (ids.length > 0 && !isEdit) {
      setValue('categoryIds', ids);
    }
  }, [selectedOffer?.categories, setValue, isEdit]);

  return (
    <Modal
      dense
      size="fullscreen"
      title={isEdit ? t('textCreative:actions.edit') : t('textCreative:actions.add')}
      buttonProps={{
        className: classnames({ 'btn-rounded': !isEdit, 'p-0 text-primary': isEdit }),
        color: isEdit ? 'link' : 'primary',
        size: 'sm',
        id: isEdit ? `edit-text-creative-button-${defaultValues.id}` : '',
        title: isEdit && t('::textCreative:actions.edit'),
        variant: isEdit ? 'edit' : 'add',
        ...buttonProps,
      }}
      scrollable
      footer
      footerVariant="submit"
      submitting={submitting}
      onSubmit={handleSubmit(handleSubmitForm)}
      isOpen={isOpen}
      onToggle={setIsOpen}
    >
      <Form {...otherProps}>
        {connectForm(
          <Row>
            <Col xs={12} lg={5}>
              <CardBasic className="border border-light " title={t('landingPageTitle')}>
                <FormGroup label={t('offer')} required>
                  <OfferSearchInput
                    loadInitial
                    forTextCreative
                    name="offerId"
                    active={!isEdit}
                    searchParams={offerSearchParams}
                    required
                  />
                </FormGroup>
                {!!offerVariants.length && (
                  <FormGroup label={t('::common:model.offerVariant')} required>
                    <OfferVariantRadioGroup
                      name="offerVariantId"
                      offerVariants={offerVariants}
                      rules={{ required: true }}
                    />
                  </FormGroup>
                )}
                <FormGroup label={t('locales')} required>
                  <LocaleCheckInput name="locales" rules={{ required: true }} />
                </FormGroup>
                <FormGroup label={t('categoryIds')}>
                  <ControlGroup name="categoryIds">
                    <CategorySelect name="categoryIds" multi rules={{ maxLength: 5 }} />
                  </ControlGroup>
                </FormGroup>
              </CardBasic>
              <CardBasic className="border border-light " title={t('campaignPeriodTitle')}>
                {isInfinityTime && (
                  <FormGroup label={t('publishedAt')}>
                    <DateTimeSelect
                      name="publishedAt"
                      closeOnSelect={false}
                      future
                      timeFormat="HH:mm"
                      inputProps={{ autoComplete: 'off' }}
                    />
                  </FormGroup>
                )}
                <ActivePeriodDateTimePicker
                  disablePastDate
                  names={{
                    start: 'activeDateStart',
                    end: 'activeDateEnd',
                    infinity: 'isInfinityTime',
                  }}
                />
              </CardBasic>
              <CardBasic className="border border-light " title={t('creativeTitle')}>
                <FormGroup label={t('creativeName')} required>
                  <Input
                    type="text"
                    name="creativeName"
                    rules={{ required: true, maxLength: 20 }}
                  />
                </FormGroup>
                <FormGroup label={t('title')} required>
                  <Input type="text" name="title" rules={{ required: true, maxLength: 20 }} />
                </FormGroup>
                <FormGroup label={t('content1')} required>
                  <Input type="text" name="content1" rules={{ required: true, maxLength: 25 }} />
                </FormGroup>
                <FormGroup label={t('couponCode')}>
                  <Input type="text" name="couponCode" />
                </FormGroup>
                <FormGroup label={t('currency')}>
                  <CurrencySelect name="currencyId" />
                </FormGroup>
                <FormGroup label={t('originalPrice')}>
                  <Input type="text" name="originalPrice" />
                </FormGroup>
                <FormGroup label={t('discountPrice')}>
                  <Input type="text" name="discountPrice" />
                </FormGroup>
                {selectedOffer?.deeplinkable && (
                  <FormGroup label={t('clientUrl')} required>
                    <DeepLinkInput
                      name="clientUrl"
                      domain={selectedOffer.destinationDomains}
                      rules={{ required: true }}
                    />
                  </FormGroup>
                )}
                <FormGroup label={t('buttonText')} required>
                  <Input type="text" name="buttonText" rules={{ required: true }} />
                </FormGroup>
              </CardBasic>
              <CardBasic className="border border-light" title={t('imageUploadTitle')}>
                <FileUploader
                  image
                  maxSize={204800}
                  entity="textCreative"
                  onSuccess={onImageUpload}
                  previewLinks={imageUrl}
                  disabled={!offerId || !offerVariantId}
                />
              </CardBasic>
            </Col>
            <Col xs={12} lg={7}>
              <TextCreativePreview creative={creative} />
            </Col>
          </Row>
        )}
      </Form>
    </Modal>
  );
};

export default TextCreativeFormButton;
